import React, { useState } from 'react';
import { RotateCw, Star, Trash2, RefreshCw, Info } from 'lucide-react';
import placeholder from '../../images/photo-placeholder.svg';
import styles from './photo-card.module.css';

interface PhotoCardProps {
    id: number;
    src: string;
    isMain: boolean;
    isActionsOpen: boolean;
    disabled?: boolean;
    canDelete?: boolean;
    status?: 'IN_MODER' | 'SUCCESS' | 'CANCELED';
    rejectReason?: string | null;
    onToggleActions: (id: number) => void;
    onRotate: () => void;
    onSetMain: () => void;
    onDelete: () => void;
    onReplace: (file: File) => void;
}

const PhotoCard: React.FC<PhotoCardProps> = ({
    id,
    src,
    isMain,
    isActionsOpen,
    disabled,
    canDelete = true,
    status = 'SUCCESS',
    rejectReason,
    onToggleActions,
    onRotate,
    onSetMain,
    onDelete,
    onReplace,
}) => {
    const [isRejectReasonOpen, setIsRejectReasonOpen] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const handleReplaceClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onReplace(file);
        }
        e.target.value = '';
    };

    const handleReasonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setIsRejectReasonOpen(true);
    };

    return (
        <>
            <div
                className={styles.card}
                onClick={() => onToggleActions(id)}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    className={styles.fileInput}
                    accept="image/*"
                    onChange={handleFileChange}
                />

                {isMain && (
                    <div className={styles.mainBadge}>
                        <Star size={14} />
                    </div>
                )}

                <div className={`${styles.image} ${status === 'CANCELED' ? styles.rejected : ''}`}>
                    <img src={src || placeholder} alt="" />
                    {status === 'IN_MODER' && (
                        <div className={styles.statusBadge}>На модерации</div>
                    )}
                    {status === 'CANCELED' && (
                        <div className={styles.statusBadgeDanger}>
                            <span>Отклонено</span>
                            {rejectReason && (
                                <button
                                    type="button"
                                    className={styles.reasonButton}
                                    onClick={handleReasonClick}
                                    aria-label="Показать причину отклонения"
                                >
                                    <Info size={14} />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {isActionsOpen && (
                    <div className={styles.actions}>
                        {status !== 'CANCELED' && (
                            <button disabled={disabled} onClick={onRotate}>
                                <RotateCw size={18} />
                            </button>
                        )}

                        {!isMain && status === 'SUCCESS' && (
                            <button disabled={disabled} onClick={onSetMain}>
                                <Star size={18} />
                            </button>
                        )}

                        {status === 'SUCCESS' && (
                            <button
                                disabled={disabled}
                                onClick={handleReplaceClick}
                                title="Заменить"
                            >
                                <RefreshCw size={18} />
                            </button>
                        )}

                        {canDelete && (
                            <button
                                className={styles.delete}
                                disabled={disabled}
                                onClick={onDelete}
                            >
                                <Trash2 size={18} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isRejectReasonOpen && (
                <div
                    className={styles.reasonModalOverlay}
                    onClick={() => setIsRejectReasonOpen(false)}
                >
                    <div
                        className={styles.reasonModal}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Причина отклонения фото"
                    >
                        <div className={styles.reasonModalTitle}>Фото отклонено</div>
                        <div className={styles.reasonModalText}>
                            {rejectReason}
                        </div>
                        <button
                            type="button"
                            className={styles.reasonModalClose}
                            onClick={() => setIsRejectReasonOpen(false)}
                        >
                            Понятно
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default PhotoCard;
